import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Download,
  FileDown,
  History,
  Loader2,
  RefreshCw,
  Search,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/page-header";
import { RefreshButton } from "@/components/data/refresh-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DataTable, type Column } from "@/components/data/data-table";
import { EmptyState } from "@/components/data/empty-state";
import {
  INQUIRY_TONE,
  InquiryStatusBadge,
  TONE_CLASSES,
  toneDotClass,
} from "@/components/data/status-badge";
import {
  downloadInquiryTemplate,
  INQUIRY_CRITERIA_FIELDS,
  inquiriesApi,
  isInquiryActive,
  type Inquiry,
  type InquiryCriteria,
  type InquiryStatus,
} from "@/api/inquiries";
import { queryKeys } from "@/api/query-keys";
import { useCursorList } from "@/hooks/use-cursor-list";
import { DateRangeFilter, dayToIso } from "@/components/data/date-range-filter";
import { authErrorMessage } from "@/auth/errors";
import { UploadLayoutDialog } from "./-dialogs/upload-layout-dialog";
import { ExportsDock } from "./-components/exports-dock";
import { formatDateTime } from "@/lib/format";
import {
  consultasSearchSchema,
  estadosAUrl,
  estadosDe,
  rangoVisibleDe,
} from "@/lib/consultas-filtro";


export const Route = createFileRoute("/_authenticated/consultas/")({
  validateSearch: consultasSearchSchema,
  component: ConsultasPage,
});

const CRITERIOS_VACIOS: InquiryCriteria = {};

/** Tamaño de página del listado. */
const LIMITE = 25;

function ConsultasPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [criterios, setCriterios] = useState<InquiryCriteria>(CRITERIOS_VACIOS);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [exportsOpen, setExportsOpen] = useState(false);
  const [descargandoPlantilla, setDescargandoPlantilla] = useState(false);
  const search = Route.useSearch();
  const navigateAqui = Route.useNavigate();
  // Rango en ISO. SIN rango el backend cae al año en curso, así que la pantalla no preselecciona
  // nada: es el mismo default que el listado de bajas y evita que un rango invisible haga
  // parecer que faltan consultas.
  const rango = { from: dayToIso(search.from, "start"), to: dayToIso(search.to, "end") };
  // Reintento de una consulta fallida: vuelve a la cola con el MISMO id, así que el
  // histórico no se llena de duplicados. Se invalidan listado y conteos porque los dos
  // cambian: la fila pasa a "Pendiente" y el chip de fallidas baja en uno.
  const reintento = useMutation({
    mutationFn: (id: string) => inquiriesApi.retry(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.consultas.all });
      toast.success(t("consultas.retryToast"));
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  // Exportación asíncrona: se pide con el filtro que se ve en pantalla y el archivo lo arma
  // un worker. Al crearla se abre el cajón, que es donde aparecerá cuando esté lista.
  const exportMutation = useMutation({
    mutationFn: inquiriesApi.createExport,
    onSuccess: () => {
      setExportsOpen(true);
      queryClient.invalidateQueries({ queryKey: queryKeys.consultas.exports() });
      toast.success(t("consultas.exports.started"));
    },
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const rangoVisible = rangoVisibleDe(search);
  const hayRango = !!rangoVisible.from || !!rangoVisible.to;
  // Los chips son multi-selección, como en inscripciones y bajas. Con UNO el backend
  // conserva la paginación real; con varios devuelve la unión completa del rango.
  const estados = estadosDe(search) as InquiryStatus[];
  const paramsListado = {
    limit: LIMITE,
    ...rango,
    ...(search.year !== undefined ? { year: search.year } : {}),
    ...(estados.length ? { status: estados.join(",") } : {}),
  };

  // Los conteos van con el MISMO rango que la tabla, no con el estado: los chips tienen que
  // seguir mostrando cuántas hay de cada estado incluso cuando uno ya está filtrando.
  // Los conteos van con el rango VISIBLE, no con `rango`: un enlace con `?year=2025` no trae
  // `from`/`to`, así que contar por `rango` mediría el año en curso mientras la tabla muestra
  // 2025. Es exactamente la divergencia que estos chips vienen a evitar.
  //
  // El estado NO va: los chips deben seguir diciendo cuántas hay de cada uno incluso cuando uno
  // ya está filtrando; si no, al activar un chip los demás se irían a cero.
  const paramsConteos = {
    from: dayToIso(rangoVisible.from, "start"),
    to: dayToIso(rangoVisible.to, "end"),
  };
  const conteos = useQuery({
    queryKey: queryKeys.consultas.counts(paramsConteos),
    queryFn: () => inquiriesApi.counts(paramsConteos),
  });
  const counts = conteos.data;

  const alternarEstado = (valor: InquiryStatus) => {
    const siguiente = estados.includes(valor)
      ? estados.filter((s) => s !== valor)
      : [...estados, valor];
    void navigateAqui({ search: (prev) => ({ ...prev, status: estadosAUrl(siguiente) }) });
  };
  const limpiarEstados = () =>
    void navigateAqui({ search: (prev) => ({ ...prev, status: undefined }) });

  const lista = useCursorList({
    // Los parámetros VAN en la llave: si no, dos listados con filtros distintos
    // comparten entrada de caché y el segundo muestra los datos del primero. Hoy los
    // parámetros son constantes, pero el filtro por estado de la Entrega C entraría
    // justo por aquí.
    // El año VA en la llave: si no, cambiar de año mostraría la caché del anterior.
    queryKey: queryKeys.consultas.list(paramsListado),
    fetcher: (params) => inquiriesApi.list(params),
    params: paramsListado,
    // Mientras alguna consulta siga trabajando, el listado se refresca solo. Al
    // terminar todas, el intervalo se apaga: sin esta condición la pantalla seguiría
    // pidiendo al servidor indefinidamente. Se evalúa sobre TODAS las páginas
    // cargadas, no solo la primera, o una consulta activa en la página 2 dejaría de
    // actualizarse sola.
    refetchInterval: (items) =>
      items.some((i) => isInquiryActive(i.status)) ? 5_000 : false,
  });

  // Al menos un criterio con contenido real. Es el mismo criterio que aplica el
  // backend; aquí solo evita el viaje y el error.
  const hayCriterio = INQUIRY_CRITERIA_FIELDS.some((k) => (criterios[k] ?? "").trim());

  const crear = useMutation({
    mutationFn: () => inquiriesApi.create(criterios),
    onSuccess: (inquiry) => {
      setCriterios(CRITERIOS_VACIOS);
      void queryClient.invalidateQueries({ queryKey: queryKeys.consultas.all });
      toast.success(t("consultas.toast.created"));
      void navigate({
        to: "/consultas/$inquiryId",
        params: { inquiryId: inquiry.id },
      });
    },
    // `authErrorMessage` es lo que usa el resto del panel: traduce los códigos de
    // Cognito (sesión expirada, permiso denegado) en vez de enseñar el error crudo.
    onError: (err) => toast.error(authErrorMessage(err)),
  });

  const columnas: Column<Inquiry>[] = [
    {
      id: "criterios",
      header: t("consultas.table.criteria"),
      cell: (row) => (
        <div className="flex flex-wrap gap-1">
          {INQUIRY_CRITERIA_FIELDS.filter((k) => row.criteria?.[k]).map((k) => (
            <span
              key={k}
              className="rounded border border-border bg-muted px-1.5 py-0.5 text-xs"
            >
              <span className="text-muted-foreground">{t(`consultas.criteria.${k}`)}: </span>
              <span className="font-mono">{row.criteria[k]}</span>
            </span>
          ))}
        </div>
      ),
    },
    {
      id: "estado",
      header: t("consultas.table.status"),
      cell: (row) => <InquiryStatusBadge status={row.status} />,
    },
    {
      id: "resultados",
      header: t("consultas.table.results"),
      cell: (row) =>
        // También con `failed`: un `0` en esa columna es indistinguible de una
        // búsqueda que corrió bien y no encontró nada.
        isInquiryActive(row.status) || row.status === "failed" ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          // Una sola cadena interpolada, no número + texto pegados: el orden de las
          // palabras cambia entre idiomas y concatenar lo rompe en chino.
          <span>
            {row.truncated && row.totalPortal !== undefined
              ? t("consultas.shownOfTotal", {
                  shown: row.resultCount,
                  total: row.totalPortal,
                })
              : row.resultCount}
          </span>
        ),
    },
    {
      id: "fecha",
      header: t("consultas.table.createdAt"),
      cell: (row) => <span className="text-sm">{formatDateTime(row.createdAt)}</span>,
    },
    {
      id: "actions",
      header: "",
      // Solo en las fallidas: en cualquier otro estado el backend responde 400, así que
      // pintar el botón sería ofrecer algo que no se puede hacer. Mismo criterio y mismo
      // lugar que en inscripciones y bajas.
      cell: (row) =>
        row.status === "failed" ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={reintento.isPending}
            onClick={(e) => {
              // La fila entera es clickeable (abre el detalle); sin esto, reintentar
              // navegaría al mismo tiempo.
              e.stopPropagation();
              reintento.mutate(row.id);
            }}
          >
            <RefreshCw className="size-3.5" />
            {t("consultas.actions.retry")}
          </Button>
        ) : null,
      headClassName: "w-32",
      className: "text-right",
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("consultas.title")}
        actions={
          <div className="flex items-center gap-2">
            <DateRangeFilter
              from={rangoVisible.from}
              to={rangoVisible.to}
              // Se limpia `year` al tocar el picker: dejar los dos puestos haría que el atajo
              // del año ganara sobre el rango que el operador acaba de elegir.
              onChange={(r) =>
                void navigateAqui({ search: { from: r.from, to: r.to, year: undefined } })
              }
            />
            <RefreshButton
              onClick={() => {
                void lista.refetch();
                // Los chips también: refrescar solo la tabla los dejaría describiendo un
                // estado anterior del catálogo.
                void conteos.refetch();
              }}
              spinning={lista.isFetching || conteos.isFetching}
            />
            <Button
              variant="outline"
              disabled={exportMutation.isPending}
              onClick={() =>
                // El MISMO filtro que la tabla, incluido el año cuando viene de un enlace
                // viejo: el archivo tiene que cuadrar con lo que está en pantalla.
                exportMutation.mutate({
                  // Rango y estados, los MISMOS que la tabla: el archivo tiene que cuadrar
                  // con lo que está en pantalla, no con un subconjunto.
                  filter: { ...paramsConteos, ...(estados.length ? { statuses: estados } : {}) },
                })
              }
            >
              {exportMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <FileDown className="size-4" />
              )}
              {t("consultas.exports.action")}
            </Button>
            <Button
              variant="outline"
              size="icon"
              aria-label={t("consultas.exports.title")}
              onClick={() => setExportsOpen(true)}
            >
              <History className="size-4" />
            </Button>
            <Button
              variant="outline"
              disabled={descargandoPlantilla}
              onClick={async () => {
                setDescargandoPlantilla(true);
                try {
                  await downloadInquiryTemplate();
                } catch (err) {
                  toast.error(authErrorMessage(err));
                } finally {
                  setDescargandoPlantilla(false);
                }
              }}
            >
              {descargandoPlantilla ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Download className="size-4" />
              )}
              {t("consultas.upload.downloadTemplate")}
            </Button>
            <Button onClick={() => setUploadOpen(true)}>
              <Upload className="size-4" />
              {t("consultas.upload.action")}
            </Button>
          </div>
        }
      />

      <ExportsDock open={exportsOpen} onOpenChange={setExportsOpen} />

      <UploadLayoutDialog open={uploadOpen} onOpenChange={setUploadOpen} />

      <div className="rounded-lg border border-border bg-card p-4">
        <h2 className="mb-1 text-sm font-medium">{t("consultas.form.title")}</h2>
        <p className="mb-4 text-sm text-muted-foreground">{t("consultas.form.help")}</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (hayCriterio && !crear.isPending) crear.mutate();
          }}
        >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {INQUIRY_CRITERIA_FIELDS.map((campo) => (
            <div key={campo} className="space-y-1">
              <label
                htmlFor={`criterio-${campo}`}
                className="text-xs font-medium text-muted-foreground"
              >
                {t(`consultas.criteria.${campo}`)}
              </label>
              <Input
                id={`criterio-${campo}`}
                value={criterios[campo] ?? ""}
                placeholder={t(`consultas.placeholder.${campo}`)}
                onChange={(e) =>
                  setCriterios((prev) => ({ ...prev, [campo]: e.target.value }))
                }
              />
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button type="submit" disabled={!hayCriterio || crear.isPending}>
            <Search className="mr-2 h-4 w-4" />
            {t("consultas.form.submit")}
          </Button>
          {!hayCriterio ? (
            <span className="text-sm text-muted-foreground">
              {t("consultas.form.needOne")}
            </span>
          ) : null}
        </div>
        </form>
      </div>

      {/* Chips de conteo por estado. Misma anatomía que inscripciones y bajas: "Total" limpia
          el filtro y cada chip alterna el suyo. Un estado en cero NO se pinta: el backend solo
          devuelve los mayores que cero, y un chip vacío ocupa espacio y hace dudar del filtro. */}
      {counts ? (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={limpiarEstados}
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
              estados.length === 0
                ? "border-primary bg-primary/10 font-medium text-foreground"
                : "border-input text-muted-foreground hover:bg-muted"
            }`}
          >
            {t("consultas.counts.total")}
            <span className="rounded-full bg-muted px-1.5 text-xs font-semibold text-foreground">
              {counts.total}
            </span>
          </button>
          {(Object.keys(counts.byStatus) as InquiryStatus[]).map((s) => {
            const activo = estados.includes(s);
            const tone = INQUIRY_TONE[s] ?? "neutral";
            return (
              <button
                key={s}
                type="button"
                onClick={() => alternarEstado(s)}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-all ${
                  TONE_CLASSES[tone]
                } ${activo ? "font-medium ring-2 ring-current ring-offset-1" : "hover:brightness-95"}`}
              >
                <span className={`size-1.5 rounded-full ${toneDotClass(tone)}`} />
                {t(`status.inquiry.${s}`, { defaultValue: s })}
                <span className="rounded-full bg-background/80 px-1.5 text-xs font-semibold text-foreground">
                  {counts.byStatus[s]}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <DataTable
        data={lista.items}
        columns={columnas}
        getRowId={(row) => row.id}
        isLoading={lista.isLoading}
        isFetching={lista.isFetching}
        error={lista.error as Error | null}
        loadMore={
          lista.hasNextPage
            ? {
                hasMore: true,
                isLoading: lista.isFetchingNextPage,
                onLoadMore: () => void lista.fetchNextPage(),
              }
            : undefined
        }
        onRowClick={(row) =>
          void navigate({ to: "/consultas/$inquiryId", params: { inquiryId: row.id } })
        }
        emptyState={
          /* Con un rango puesto, el vacío significa "no hay nada en ESTAS fechas" y no "no has
             hecho ninguna consulta". Decir lo segundo teniendo consultas fuera del rango sería
             falso, y el operador podría creer que perdió su histórico. */
          <EmptyState
            icon={Search}
            title={hayRango ? t("consultas.empty.rangeTitle") : t("consultas.empty.title")}
            description={
              hayRango ? t("consultas.empty.rangeDescription") : t("consultas.empty.description")
            }
          />
        }
      />
    </div>
  );
}
